import * as React from "react"

/** What installing the app looks like in this browser, right now. */
export enum PwaInstallAvailability {
  /** Already running as the installed app, or the browser offers no way to install. */
  Unavailable = "unavailable",
  /** The browser handed us its install prompt (Chrome, Edge, Samsung Internet): one tap installs. */
  Prompt = "prompt",
  /** iPhone or iPad: no prompt exists, so the user adds it from the Share menu themselves. */
  IosGuide = "ios_guide",
}

/** Where the Share button is on an iPhone, so the guide can point at it. */
export enum IosShareButtonPlacement {
  /** Safari on an iPhone: the bottom bar (behind "⋯" on recent iOS). */
  Bottom = "bottom",
  /** Safari on an iPad, and Chrome, Edge and Firefox on iOS: the address bar at the top. */
  Top = "top",
}

/** The result of the browser's install prompt. */
export enum PwaInstallOutcome {
  Accepted = "accepted",
  Dismissed = "dismissed",
  Unavailable = "unavailable",
}

/** Chrome's `beforeinstallprompt` event; not in the DOM typings. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

let deferredPrompt: BeforeInstallPromptEvent | null = null
let installed = false
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((listener) => listener())

/**
 * The browser fires `beforeinstallprompt` once, early in the page load, so it is caught at module load (this file
 * is imported from main.tsx) and kept until someone taps "Install". Holding it also stops Chrome's own mini
 * banner on Android, since the account menu offers the install instead.
 */
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    notify()
  })
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null
    installed = true
    notify()
  })
}

/** True inside the installed app (home screen icon), on every platform. */
export function isRunningAsInstalledApp(): boolean {
  if (typeof window === "undefined") return false
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  return iosStandalone || window.matchMedia?.("(display-mode: standalone)").matches === true
}

/** iPhone and iPad, including iPads that report themselves as a Mac. */
export function isIosDevice(): boolean {
  if (typeof navigator === "undefined") return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
}

/** Safari on an iPhone keeps Share in its bottom bar; every other iOS browser, and Safari on an iPad, at the top. */
export function iosShareButtonPlacement(): IosShareButtonPlacement {
  const ua = navigator.userAgent
  const iPad = /iPad/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  return iPad || /CriOS|EdgiOS|FxiOS/.test(ua) ? IosShareButtonPlacement.Top : IosShareButtonPlacement.Bottom
}

function currentAvailability(): PwaInstallAvailability {
  if (installed || isRunningAsInstalledApp()) return PwaInstallAvailability.Unavailable
  if (deferredPrompt) return PwaInstallAvailability.Prompt
  if (isIosDevice()) return PwaInstallAvailability.IosGuide
  return PwaInstallAvailability.Unavailable
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Opens the browser's install prompt and waits for the answer. The prompt can only be used once. */
async function promptInstall(): Promise<PwaInstallOutcome> {
  const event = deferredPrompt
  if (!event) return PwaInstallOutcome.Unavailable
  deferredPrompt = null
  notify()
  await event.prompt()
  const choice = await event.userChoice
  return choice.outcome === "accepted" ? PwaInstallOutcome.Accepted : PwaInstallOutcome.Dismissed
}

/** Whether (and how) this browser can install the app, kept up to date as the prompt arrives or the app installs. */
export function usePwaInstall() {
  const availability = React.useSyncExternalStore(subscribe, currentAvailability, () => PwaInstallAvailability.Unavailable)
  return { availability, promptInstall }
}
